import { createTheme, alpha } from '@mui/material/styles';



export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#0a192f',
      light: '#172a45',
      dark: '#020c1b',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#ffffff', // ✅ was #000000 — black on orange is harsh
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f5f7fa',
      paper: '#ffffff',
    },
    text: {
      primary: '#0a192f',
      secondary: '#495057',
    },
    divider: '#dee2e6',
    warning: {
      main: '#ff9800',
    },
    action: {
      hover: 'rgba(10, 25, 47, 0.04)',   // ✅ Primary-tinted hover
      focus: 'rgba(10, 25, 47, 0.02)',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#0a192f',
      '@media (max-width:600px)': { fontSize: '1.75rem' },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#0a192f',
      '@media (max-width:600px)': { fontSize: '1.4rem' },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#172a45',
    },
    h6: {
      fontWeight: 500,
      color: '#020c1b',
    },
    body1: {
      fontSize: '1rem',
      color: '#495057',
      '@media (max-width:600px)': { fontSize: '1rem' },
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: { borderRadius: 4 },
  spacing: 8,
  transitions: { duration: { standard: 250 } },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#f5f7fa' },
      },
    },

    // ✅ Single unified MuiButton block — no more duplicate
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            width: '100%',
            padding: '10px 16px',
            fontSize: '0.95rem',
          },
        }),
        containedPrimary: {
          backgroundColor: '#0a192f',
          '&:hover': { backgroundColor: '#172a45' },
        },
        containedSecondary: {
          '&:hover': { backgroundColor: '#ffa726' },
        },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0a192f',
          color: '#ffffff',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          // ✅ Removed borderRadius — AppBars are full-width, radius causes clipping
        },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: { boxShadow: 'none' },
        }),
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          border: '1px solid #dee2e6', // ✅ Matches divider — gives cards definition on #f5f7fa
          padding: 2,
          [theme.breakpoints.down('sm')]: {
            borderRadius: 0,
            borderLeft: 'none',
            borderRight: 'none',
            boxShadow: 'none',
          },
        }),
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 12,
          '&:last-child': { paddingBottom: 24 },
          [theme.breakpoints.down('sm')]: {
            padding: 0,
            '&:last-child': { paddingBottom: 12 },
          },
        }),
      },
    },

    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 80,
            fontSize: '0.8rem',
            padding: '6px 12px',
          },
        }),
      },
    },

    // ✅ Fixed selected state — navy alpha instead of orange
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
            },
          },
        }),
      },
    },

    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#f1f3f5',
          color: '#0a192f',
        },
        input: { color: '#0a192f' },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#ced4da',
        },
        // ✅ Guided hover/focus border colors to match primary
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0a192f',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#0a192f',
          },
        },
      },
    },

    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: {
          color: '#495057', // ✅ Matches text.secondary
          '&.Mui-focused': { color: '#0a192f' },
        },
      },
    },

    // ✅ Consistent with dark theme
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#dee2e6' },
      },
    },

    MuiChip: {
      styleOverrides: {
        outlined: {
          borderColor: '#dee2e6',
        },
      },
    },
  },
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#4f9cf9',      // ✅ Light blue — visible against dark navy backgrounds
      light: '#82bfff',
      dark: '#1976d2',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#f57c00',
      light: '#ff9800',
      dark: '#bb4d00',
      contrastText: '#ffffff', // ✅ was #000000 — unreadable on orange in dark mode
    },
    accent: {
      main: '#4b6584',
      contrastText: '#ffffff',
    },
    background: {
      default: '#0d1b2a',    // ✅ Slightly off-black — easier on the eyes than pure navy
      paper: '#1a2d42',      // ✅ Noticeably lifted from default for depth
    },
    text: {
      primary: '#e8edf2',    // ✅ Soft white — less harsh than #ffffff
      secondary: '#8ba3bc',  // ✅ Muted blue-grey
    },
    divider: '#2a3f55',      // ✅ More visible than #1e2a3a
    warning: {
      main: '#ff9800',
    },
    action: {
      hover: 'rgba(79, 156, 249, 0.06)',   // ✅ Tinted hover to match new primary
      focus: 'rgba(79, 156, 249, 0.03)',
    },
  },
  typography: {
    fontFamily: '"Lato", "Helvetica Neue", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.75rem',
      color: '#e8edf2',      // ✅ Fixed — was hardcoded navy (#0a192f), invisible in dark
      '@media (max-width:600px)': { fontSize: '1.75rem' },
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#e8edf2',      // ✅ Fixed
      '@media (max-width:600px)': { fontSize: '1.4rem' },
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#e0e6ed',
    },
    h6: {
      fontWeight: 500,
      color: '#c5d5e8',      // ✅ Brighter than #aab8c2
    },
    body1: {
      fontSize: '1rem',
      color: '#8ba3bc',
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  shape: { borderRadius: 4 },
  spacing: 8,
  transitions: { duration: { standard: 250 } },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: '#0d1b2a' },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: { width: '100%' },
        }),
        // ✅ Primary buttons now use the lighter blue so they're visible
        containedPrimary: {
          backgroundColor: '#4f9cf9',
          color: '#ffffff',
          '&:hover': { backgroundColor: '#82bfff' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundImage: 'none',  // ✅ MUI adds a white overlay in dark mode by default — remove it
          [theme.breakpoints.down('sm')]: { boxShadow: 'none' },
        }),
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 4,
          border: '1px solid #2a3f55',  // ✅ Subtle border gives cards definition in dark mode
          padding: 2,
          [theme.breakpoints.down('sm')]: {
            borderRadius: 2,
            borderLeft: 'none',
            borderRight: 'none',
            boxShadow: 'none',
          },
        }),
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: 12,
          '&:last-child': { paddingBottom: 24 },
          [theme.breakpoints.down('sm')]: {
            padding: 2,
            '&:last-child': { paddingBottom: 12 },
          },
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#0d1b2a',
          borderBottom: '1px solid #2a3f55', // ✅ Border separates it from page content
          color: '#ffffff',
          boxShadow: 'none',                 // ✅ Flat is cleaner in dark mode
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: {
            minWidth: 80,
            fontSize: '0.8rem',
            padding: '6px 12px',
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#0f2035', // ✅ Slightly deeper than paper for inset feel
          color: '#e8edf2',
        },
        input: { color: '#e8edf2' },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#2a3f55',
        },
        root: {
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4f9cf9',  // ✅ Primary-tinted hover border
          },
        },
      },
    },
    MuiInputLabel: {
      defaultProps: { shrink: true },
      styleOverrides: {
        root: { color: '#8ba3bc' },  // ✅ Visible label color in dark mode
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&.Mui-selected': {
            backgroundColor: 'rgba(79, 156, 249, 0.12)', // ✅ Matches new primary
            color: '#4f9cf9',
          },
        }),
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: '#2a3f55' }, // ✅ Consistent divider color
      },
    },
    MuiChip: {
      styleOverrides: {
        outlined: {
          borderColor: '#2a3f55', // ✅ Chips don't disappear in dark mode
        },
      },
    },
  },
});


